import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import SignaturePad from '../../components/SignaturePad';
import { CheckCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function calcAge(dob) {
  if (!dob) return '';
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : '';
}

function fmtDT(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const DECL = [
  'I confirm that the above filled information is true to the best of my knowledge.',
  'I agree that the teacher will always guide me to the best of her ability and any injury due to exercises/asanas during class will not be the responsibility of the teacher.',
  'I agree that the fees once paid is non-refundable.',
];

const INPUT = 'bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm';

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-card border border-[#E7E5E4]/50 space-y-4">
      {title && <h3 className="font-heading font-semibold text-[#292524] text-base border-b border-[#E7E5E4]/60 pb-2 mb-4">{title}</h3>}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#57534E] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ReadOnly({ value }) {
  return <p className="text-[#292524] font-body text-sm bg-[#FFFAF0] border border-[#E7E5E4] rounded-2xl px-4 py-3 min-h-[44px]">{value || '—'}</p>;
}

// ── Read-only submitted view ──────────────────────────────────────────────────
function ProfileReadOnly({ profile, user }) {
  const age = calcAge(profile.date_of_birth);
  return (
    <div className="space-y-5">
      {/* Submitted badge */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 border border-green-200 rounded-full px-4 py-1.5 text-sm font-heading font-medium">
          <CheckCircle className="w-4 h-4" strokeWidth={2} /> Submitted
        </span>
        <span className="text-xs text-[#78716C] font-body">Profile Completed On: <strong>{fmtDT(profile.submitted_at)}</strong></span>
      </div>

      <SectionCard title="Personal Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Student Name"><ReadOnly value={user?.name} /></Field>
          <Field label="Mobile Number"><ReadOnly value={user?.phone} /></Field>
          <Field label="Email"><ReadOnly value={profile.email} /></Field>
          <Field label="Joining Date"><ReadOnly value={profile.joining_date} /></Field>
          <Field label="Date of Birth"><ReadOnly value={profile.date_of_birth} /></Field>
          <Field label="Age (Auto-calculated)"><ReadOnly value={age ? `${age} years` : '—'} /></Field>
          <Field label="Emergency Contact Number"><ReadOnly value={profile.emergency_contact} /></Field>
        </div>
        <Field label="Address"><ReadOnly value={profile.address} /></Field>
      </SectionCard>

      <SectionCard title="Medical Details">
        <Field label="Ongoing / Recent Medications"><ReadOnly value={profile.medications} /></Field>
        <Field label="Health Concerns"><ReadOnly value={profile.health_concerns} /></Field>
      </SectionCard>

      <SectionCard title="Declarations">
        {DECL.map((text, i) => (
          <div key={i} className="flex items-start gap-3 bg-green-50/50 rounded-2xl p-3 border border-green-100">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-sm text-[#292524] font-body leading-relaxed">{text}</p>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Signature">
        {profile.signature_data
          ? <img src={profile.signature_data} alt="Signature" className="rounded-2xl border border-[#E7E5E4] max-w-sm bg-white" />
          : <p className="text-[#A8A29E] font-body text-sm">No signature on file</p>
        }
      </SectionCard>
    </div>
  );
}

// ── Form view ─────────────────────────────────────────────────────────────────
export default function StudentProfile({ onComplete }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(undefined); // undefined=loading
  const [form, setForm] = useState({
    email: '', dob: '', joining_date: new Date().toISOString().split('T')[0], emergency: '', address: '', medications: '', health_concerns: '',
    d1: false, d2: false, d3: false,
  });
  const [sig, setSig] = useState(null);
  const [errors, setErrors] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/student/profile`, { headers: getAuthHeaders() })
      .then(r => setProfile(r.data))
      .catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  const age = calcAge(form.dob);

  const validate = () => {
    const e = {};
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email is required.';
    if (!form.joining_date) e.joining_date = 'Joining date is required.';
    if (!form.dob) e.dob = 'Date of birth is required.';
    if (!form.emergency.trim()) e.emergency = 'Emergency contact is required.';
    if (!form.address.trim()) e.address = 'Address is required.';
    if (!form.medications.trim()) e.medications = 'Please fill this field (type "None" if not applicable).';
    if (!form.health_concerns.trim()) e.health_concerns = 'Please fill this field (type "None" if not applicable).';
    if (!form.d1) e.d1 = 'Declaration required.';
    if (!form.d2) e.d2 = 'Declaration required.';
    if (!form.d3) e.d3 = 'Declaration required.';
    if (!sig) e.sig = 'Signature is required.';
    return e;
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/student/profile`, {
        email: form.email.trim(),
        date_of_birth: form.dob,
        joining_date: form.joining_date,
        emergency_contact: form.emergency.trim(),
        address: form.address.trim(),
        medications: form.medications.trim(),
        health_concerns: form.health_concerns.trim(),
        declaration_1: form.d1,
        declaration_2: form.d2,
        declaration_3: form.d3,
        signature_data: sig,
      }, { headers: getAuthHeaders() });
      setProfile(res.data);
      setShowConfirm(false);
      setToast({ message: 'Profile Details submitted successfully.', type: 'success' });
      onComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Submission failed. Try again.', type: 'error' });
      setShowConfirm(false);
    } finally { setSubmitting(false); }
  };

  if (profile === undefined) {
    return <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    setErrors(errs => { const n = { ...errs }; delete n[field]; return n; });
  };

  const Err = ({ k }) => errors[k] ? <p className="text-xs text-red-500 mt-1 font-body">{errors[k]}</p> : null;

  return (
    <div className="space-y-5 animate-fade-in-up">
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
          ${toast.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {toast.message}
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">My Account</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Profile Details</h1>
      </div>

      {/* Mandatory banner — only shown when profile not yet submitted */}
      {!profile && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm text-amber-800 font-body font-medium">
            Please complete your Profile Details before continuing. This information is mandatory.
          </p>
        </div>
      )}

      {/* Read-only view after submission */}
      {profile ? (
        <ProfileReadOnly profile={profile} user={user} />
      ) : (
        /* ── Editable form ── */
        <form onSubmit={handleSubmitClick} className="space-y-5">

          <SectionCard title="Personal Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Student Name">
                <input value={user?.name || ''} readOnly className={`${INPUT} bg-[#F5F5F4] text-[#78716C] cursor-not-allowed`} />
              </Field>
              <Field label="Mobile Number">
                <input value={user?.phone || ''} readOnly className={`${INPUT} bg-[#F5F5F4] text-[#78716C] cursor-not-allowed`} />
              </Field>
              <div>
                <Field label="Email ID *">
                  <input type="email" placeholder="you@example.com" value={form.email} onChange={set('email')}
                    className={INPUT} />
                  <Err k="email" />
                </Field>
              </div>
              <div>
                <Field label="Joining Date *">
                  <input type="date" value={form.joining_date} onChange={set('joining_date')} className={INPUT} />
                  <Err k="joining_date" />
                </Field>
              </div>
              <div>
                <Field label="Date of Birth *">
                  <input type="date" value={form.dob} onChange={set('dob')} className={INPUT} max={new Date().toISOString().split('T')[0]} />
                  <Err k="dob" />
                </Field>
              </div>
              <Field label="Age (Auto-calculated)">
                <input value={age ? `${age} years` : ''} readOnly placeholder="Select date of birth"
                  className={`${INPUT} bg-[#F5F5F4] text-[#78716C] cursor-not-allowed`} />
              </Field>
              <div>
                <Field label="Emergency Contact Number *">
                  <input type="tel" placeholder="Emergency phone number" value={form.emergency} onChange={set('emergency')} className={INPUT} />
                  <Err k="emergency" />
                </Field>
              </div>
            </div>
            <div>
              <Field label="Address *">
                <textarea rows={3} placeholder="Your full address" value={form.address} onChange={set('address')}
                  className={`${INPUT} resize-none`} />
                <Err k="address" />
              </Field>
            </div>
          </SectionCard>

          <SectionCard title="Medical Details">
            <div>
              <Field label="Ongoing / Recent Medications *">
                <p className="text-xs text-[#A8A29E] font-body mb-1.5">If none, please type 'None'.</p>
                <textarea rows={3} value={form.medications} onChange={set('medications')}
                  className={`${INPUT} resize-none`} />
                <Err k="medications" />
              </Field>
            </div>
            <div>
              <Field label="Health Concerns *">
                <p className="text-xs text-[#A8A29E] font-body mb-1.5">Any health concerns like surgeries or injuries? If none, please type 'None'.</p>
                <textarea rows={3} value={form.health_concerns} onChange={set('health_concerns')}
                  className={`${INPUT} resize-none`} />
                <Err k="health_concerns" />
              </Field>
            </div>
          </SectionCard>

          <SectionCard title="Declarations">
            {DECL.map((text, i) => {
              const key = `d${i + 1}`;
              return (
                <div key={i} className={`rounded-2xl p-4 border ${errors[key] ? 'border-red-300 bg-red-50/30' : 'border-[#E7E5E4] bg-[#FFFAF0]/50'}`}>
                  <p className="text-sm text-[#292524] font-body leading-relaxed mb-3">{text}</p>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={form[key]} onChange={set(key)} className="w-4 h-4 accent-[#F59E0B] cursor-pointer" />
                    <span className="text-sm font-body text-[#292524] font-medium">I Agree</span>
                  </label>
                  <Err k={key} />
                </div>
              );
            })}
          </SectionCard>

          <SectionCard title="Signature *">
            <p className="text-xs text-[#A8A29E] font-body mb-2">Draw your signature using mouse (desktop) or finger (mobile).</p>
            <SignaturePad onChange={setSig} disabled={false} value={null} />
            {errors.sig && <p className="text-xs text-red-500 mt-1 font-body">{errors.sig}</p>}
          </SectionCard>

          <button type="submit"
            className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-4 font-heading font-semibold text-base shadow-button hover:-translate-y-0.5 transition-all">
            Submit Profile Details
          </button>
        </form>
      )}

      {/* Confirmation dialog */}
      {showConfirm && createPortal(
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', backgroundColor:'rgba(0,0,0,0.5)' }}
          onClick={(e) => e.target === e.currentTarget && setShowConfirm(false)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl">
            <h2 className="font-heading text-xl font-semibold text-[#292524] mb-3">Confirm Submission</h2>
            <p className="text-sm text-[#57534E] font-body leading-relaxed mb-6">
              Once submitted, your Profile Details will become <strong>read-only</strong>.<br /><br />
              Please verify that all information entered is correct before continuing.<br /><br />
              Do you want to submit?
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowConfirm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
                Back & Review
              </button>
              <button type="button" onClick={handleConfirm} disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Submitting...' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
